import { Add, RemoveCircle } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormHelperText,
  FormLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import { ApiMediaInline } from "griptonite-api-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import api from "@/core/api";
import getThumbnailUrl, { getVideoThumbnailUrl } from "@/utils/getThumbnailUrl";

import FormGroup from "./FormGroup";

export type FileMedia = {
  file: File;
  type: string;
  url: string;
};
export type Media = ApiMediaInline | FileMedia;

type Props<MultT extends boolean = true> = {
  name?: string;
  media: MultT extends true ? Media[] : Media | null;
  multiple?: boolean;
  onChange: (media: MultT extends true ? Media[] : Media | null) => void;
  upload?: (
    file: File,
    onProgress: (progress: number, total: number) => void,
  ) => Promise<ApiMediaInline>;
  accept?: string;
};

const putFile = (
  url: string,
  file: File,
  onProgress: (progress: number, total: number) => void,
) => {
  const xhr = new XMLHttpRequest();
  return new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    });
    xhr.addEventListener("load", () => {
      if (Math.floor(xhr.status / 100) === 2) {
        resolve();
      } else {
        reject(new Error(xhr.statusText));
      }
    });
    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });
    xhr.addEventListener("error", () => {
      reject(new Error(xhr.statusText));
    });
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
};

const defaultUpload = async (file: File, onProgress: (progress: number, total: number) => void) => {
  const media = await api.media.createMedia({
    type: file.type,
  });
  const uploadUrl = await api.media.createSignedUploadUrl(media.uuid);
  await putFile(uploadUrl, file, onProgress);
  return { ...media, uploaded: true, uploadTime: new Date().toISOString() };
};

// Custom hook to handle retrying image loading
const useRetryableImage = (url: string | null, maxRetries = 5, initialDelay = 1000) => {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!!url);
  const retriesRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const originalUrlRef = useRef<string | null>(null);

  const attemptLoad = useRef((imgUrl: string) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!imgRef.current) {
      imgRef.current = new Image();
    }

    // Store the original URL without cache busting parameters
    if (!originalUrlRef.current) {
      // Remove any existing retry parameter if present
      originalUrlRef.current = imgUrl.split(/[?&]retry=/)[0];
    }

    const img = imgRef.current;

    img.onload = () => {
      setLoadedUrl(imgUrl);
      setIsLoading(false);
      retriesRef.current = 0;
    };

    img.onerror = () => {
      if (retriesRef.current < maxRetries) {
        const delay = initialDelay * Math.pow(1.5, retriesRef.current);
        retriesRef.current += 1;

        timerRef.current = window.setTimeout(() => {
          // Generate a clean URL with a single retry parameter
          const baseUrl = originalUrlRef.current as string;
          const hasQueryParams = baseUrl.includes("?");
          const cacheBuster = `retry=${Date.now()}`;
          const nextUrl = `${baseUrl}${hasQueryParams ? "&" : "?"}${cacheBuster}`;
          attemptLoad.current(nextUrl);
        }, delay);
      } else {
        setIsLoading(false);
        retriesRef.current = 0;
      }
    };

    img.src = imgUrl;
  });

  useEffect(() => {
    // Reset everything when URL changes
    setIsLoading(!!url);
    setLoadedUrl(null);
    retriesRef.current = 0;
    originalUrlRef.current = null;

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (url) {
      attemptLoad.current(url);
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [url]); // Only depend on url changes, not retries

  return { loadedUrl, isLoading };
};

export const MediaItemPreview = ({
  mediaItem,
}: {
  mediaItem: {
    type: string;
    url: string;
    uuid?: string;
    file?: File;
  };
}) => {
  const isVideo = mediaItem.type.startsWith("video/");
  const isImage = mediaItem.type.startsWith("image/");
  const isHeic = mediaItem.type === "image/heic";
  const isLocal = !mediaItem.url.startsWith("http");

  let previewUrl: string | null = null;
  let isVideoPreview = false;

  if (isHeic && isLocal) {
    previewUrl = null;
  } else {
    if (isLocal) {
      previewUrl = mediaItem.url;
      isVideoPreview = isVideo;
    } else {
      if (isVideo) {
        previewUrl = getVideoThumbnailUrl(mediaItem.url);
      } else {
        previewUrl = getThumbnailUrl(mediaItem.url, {
          size: 300,
          mode: "resize",
        });
      }
    }
  }

  // Use the retry hook for remote (not local) images
  const { loadedUrl, isLoading } = useRetryableImage(isLocal ? null : previewUrl);

  if (isVideoPreview && previewUrl) {
    return (
      <Box className="content">
        <video src={previewUrl} style={{ width: "100%", height: "100%" }} />
      </Box>
    );
  }

  if ((isImage || isVideo) && previewUrl) {
    return (
      <Box
        className="content"
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: 8,
          overflow: "hidden",
          "img, video": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
          },
        }}
      >
        {isLoading && !isLocal && (
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "neutral.200",
            }}
          >
            <CircularProgress size="sm" />
          </Box>
        )}
        <img
          src={isLocal ? previewUrl : loadedUrl || previewUrl}
          alt="Media preview"
          style={{ opacity: isLoading && !isLocal ? 0 : 1 }}
        />
      </Box>
    );
  }

  return (
    <Box className="content unknown-type">
      <Typography>{mediaItem.file?.name ?? "Unknown file"}</Typography>
    </Box>
  );
};

export const MediaSelect = <MultT extends boolean = true>({
  media,
  multiple,
  onChange,
  upload = defaultUpload,
  accept,
}: Props<MultT>) => {
  const mediaList = useMemo(() => {
    return Array.isArray(media) ? (media as Media[]) : ((media ? [media] : []) as Media[]);
  }, [media]);

  type OnChangeParam = MultT extends true ? Media[] : Media | null;

  const [uploadStates, setUploadStates] = useState<
    Record<string, { progress: number; total: number; error: boolean }>
  >({});

  const mediaListRef = useRef(mediaList);
  useEffect(() => {
    mediaListRef.current = mediaList;
  }, [mediaList]);

  const handleFiles = (files: FileList | File[]) => {
    let newMedia = Array.from(files).map((file) => ({
      file,
      type: file.type,
      url: URL.createObjectURL(file),
    }));
    if (!multiple) {
      newMedia = newMedia.slice(0, 1);
    }
    onChange(
      multiple
        ? ([...mediaListRef.current, ...newMedia] as OnChangeParam)
        : (newMedia[0] as OnChangeParam),
    );
    newMedia.forEach((fileMedia) => {
      upload(fileMedia.file, (progress, total) => {
        setUploadStates((states) => ({
          ...states,
          [fileMedia.file.name]: { progress, total, error: false },
        }));
      })
        .then((media) => {
          onChange(
            multiple
              ? (mediaListRef.current.map((m) =>
                  "file" in m && m.file === fileMedia.file ? media : m,
                ) as OnChangeParam)
              : (media as OnChangeParam),
          );
        })
        .catch(() => {
          setUploadStates((states) => ({
            ...states,
            [fileMedia.file.name]: { error: true, progress: 0, total: 0 },
          }));
        });
    });
  };

  return (
    <Stack
      direction="row"
      gap={1.5}
      sx={{
        ".item": {
          width: 100,
          height: 100,
          position: "relative",
          backgroundColor: "neutral.200",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 8,
          ".MuiIconButton-root": {
            position: "absolute",
            top: -12,
            right: -12,
            background: "white",
            borderRadius: "100%",
            width: 28,
            minHeight: 0,
            minWidth: 0,
            height: 28,
          },
        },
      }}
      onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!e.dataTransfer.files.length) {
          return;
        }

        // filter for files that match the accept attribute
        // accept attribute example: "image/*,.pdf"
        const files = Array.from(e.dataTransfer.files).filter((file) => {
          if (!accept) {
            return true;
          }

          const acceptTypes = accept.split(",");
          return acceptTypes.some((acceptType) => {
            if (acceptType === "image/*") {
              return file.type.startsWith("image");
            }
            if (acceptType === "video/*") {
              return file.type.startsWith("video");
            }
            return file.type === acceptType;
          });
        });

        handleFiles(files);
      }}
    >
      {mediaList.map((mediaItem) => {
        const uploadState = "file" in mediaItem ? uploadStates[mediaItem.file.name] : undefined;

        return (
          <Box className="item" key={mediaItem.url}>
            <MediaItemPreview mediaItem={mediaItem} />
            {uploadState ? (
              <Box
                sx={{
                  backgroundColor: "rgba(0,0,0,0.4)",
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 8,
                  padding: 2,
                }}
              >
                {"file" in mediaItem && uploadState && uploadState.error ? (
                  <Typography
                    level="body-sm"
                    fontWeight="bold"
                    textColor="white"
                    lineHeight={1.2}
                    textAlign="center"
                  >
                    Upload failed. <br />
                    <Link
                      sx={{
                        textDecoration: "underline",
                        color: "white",
                        marginTop: 0.5,
                      }}
                      onClick={() => {
                        setUploadStates((states) => ({
                          ...states,
                          [mediaItem.file.name]: {
                            ...uploadState,
                            error: false,
                          },
                        }));
                        if (multiple) {
                          onChange(
                            mediaListRef.current.filter(
                              (m) => "file" in m && m.file !== mediaItem.file,
                            ) as OnChangeParam,
                          );
                        }
                        setTimeout(() => {
                          handleFiles([mediaItem.file]);
                        }, 0);
                      }}
                    >
                      Retry
                    </Link>
                  </Typography>
                ) : null}
                {"file" in mediaItem && uploadState && !uploadState.error ? (
                  <CircularProgress
                    determinate
                    value={(uploadState.progress / uploadState.total) * 100}
                    sx={{
                      "--CircularProgress-progressColor": "white",
                      "--CircularProgress-trackColor": "transparent",
                    }}
                  />
                ) : null}
              </Box>
            ) : null}
            <IconButton
              onClick={() => {
                if (multiple) {
                  onChange(
                    mediaListRef.current.filter((m) => m !== mediaItem) as MultT extends true
                      ? Media[]
                      : Media | null,
                  );
                } else {
                  onChange(null as MultT extends true ? Media[] : Media | null);
                }
              }}
            >
              <RemoveCircle />
            </IconButton>
          </Box>
        );
      })}
      {multiple || !mediaList.length ? (
        <Box
          className="item"
          sx={{
            input: {
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              opacity: 0,
              cursor: "pointer",
            },
          }}
        >
          <input
            accept={accept}
            type="file"
            multiple={multiple}
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
          <Add />
        </Box>
      ) : null}
    </Stack>
  );
};

type InputProps = {
  name: string;
  label: string;
  multiple: boolean;
  accept?: string;
  helpText?: string;
};

export const MediaSelectInput = ({ name, label, multiple, accept, helpText }: InputProps) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const media = field.value as Media[] | Media | null;
  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MediaSelect<boolean>
        name={name}
        media={media}
        multiple={multiple}
        onChange={(newMedia) => field.onChange(newMedia)}
        accept={accept}
      />
      {helpText ? <FormHelperText>{helpText}</FormHelperText> : null}
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
